import { useState, type FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { KeyRound } from 'lucide-react'
import { fetchPinStatus, setOwnPin } from '../api/pin'

/**
 * Your own classroom PIN (D-396).
 *
 * **Owner:** *"the teacher pin setup would be better in the teachers account
 * view rather than having to be setup by tonya."*
 *
 * The PIN opens the kiosk register at `/room` — a teacher's own phone counts
 * as a classroom device (D-128, D-236), so this is the number she uses most
 * evenings. It could only be set through `ManageStaff`, which no teacher holds,
 * so forgetting it meant texting Tonya and waiting, ten minutes before a class.
 *
 * **Not offered on a tablet**, for the same reason a passkey is not: the device
 * is unlocked *by* a PIN, and a phone left on a counter must not be able to
 * change the one that opened it. The server refuses it there as well (D-233) —
 * this only avoids offering something that would be refused.
 *
 * **Silent for a parent.** `/api/account/pin` answers 404 with no staff row,
 * which the client reads as *no PIN*; there is no register for one to open.
 */
export default function OwnPinPanel({
  isStaff,
  isKiosk,
}: {
  isStaff: boolean
  isKiosk: boolean
}) {
  const [pin, setPin] = useState('')
  const [confirm, setConfirm] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)

  const queryClient = useQueryClient()

  const { data: status } = useQuery({
    queryKey: ['own-pin'],
    queryFn: fetchPinStatus,
    enabled: isStaff && !isKiosk,
  })

  const save = useMutation({
    mutationFn: () => setOwnPin(pin),
    onSuccess: async () => {
      setPin('')
      setConfirm('')
      setError(null)
      setSaved(true)
      await queryClient.invalidateQueries({ queryKey: ['own-pin'] })
    },
    onError: (e: Error) => {
      setError(e.message)
      setSaved(false)
    },
  })

  if (!isStaff || isKiosk) return null

  const submit = (e: FormEvent) => {
    e.preventDefault()
    setSaved(false)

    /*
     * Checked here because the server cannot: it receives one PIN and has no
     * way to know the second box disagreed. Everything else — length, digits,
     * 1111 — is the server's answer, shown in its own words.
     */
    if (pin !== confirm) {
      setError('Those two do not match.')
      return
    }

    setError(null)
    save.mutate()
  }

  return (
    <section className="mt-8 rounded-2xl border border-line bg-surface px-4 py-3.5">
      <h2 className="text-[11px] font-bold tracking-[0.11em] text-ink-3 uppercase">
        Your classroom PIN
      </h2>

      <p className="mt-1 text-[12.5px] text-ink-3">
        {status?.hasPin
          ? 'This is the number you tap in to take attendance on a studio tablet or your own phone. Set a new one here whenever you like.'
          : 'Set a number to take attendance on a studio tablet or your own phone. At least four digits.'}
      </p>

      <form onSubmit={submit} className="mt-3 flex flex-col gap-2.5">
        <label className="block">
          <span className="mb-1.5 block text-[12.5px] font-semibold text-ink-2">
            {status?.hasPin ? 'New PIN' : 'PIN'}
          </span>
          <input
            /*
             * `password`, not `text`: this is typed at a studio counter with
             * people behind you. `inputMode` still brings up the number pad.
             */
            type="password"
            inputMode="numeric"
            autoComplete="new-password"
            value={pin}
            onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
            className="w-full rounded-xl border border-line-2 bg-surface px-3 py-2.5 text-[15px]"
          />
        </label>

        <label className="block">
          <span className="mb-1.5 block text-[12.5px] font-semibold text-ink-2">Again</span>
          <input
            type="password"
            inputMode="numeric"
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value.replace(/\D/g, ''))}
            className="w-full rounded-xl border border-line-2 bg-surface px-3 py-2.5 text-[15px]"
          />
        </label>

        <button
          type="submit"
          disabled={save.isPending || pin.length === 0}
          className="flex w-full items-center justify-center gap-2 rounded-xl border border-line-2 px-4 py-2.5 text-[13.5px] font-semibold text-ink-2 hover:border-accent hover:text-accent-2 disabled:opacity-60"
        >
          <KeyRound size={15} strokeWidth={2.5} />
          {save.isPending ? 'Saving...' : status?.hasPin ? 'Change my PIN' : 'Set my PIN'}
        </button>
      </form>

      {error && (
        <p role="alert" className="mt-2 text-[12.5px] text-crit">
          {error}
        </p>
      )}

      {saved && !error && (
        <p role="status" className="mt-2 text-[12.5px] text-good">
          Saved. Use it the next time you take attendance.
        </p>
      )}
    </section>
  )
}
